/**
 * The imported site model, kept in the browser between visits.
 *
 * The STL is read locally and never uploaded: what reaches the server is the
 * measured table, a few kilobytes of sun-position cells, and that is all the
 * simulation needs. The consequence was that a reload left a measurement in
 * force with nothing on screen to inspect - the loss was in the yield and the
 * object that caused it was not in the picture, which is no way to hand someone
 * a number they have to defend.
 *
 * So the mesh is cached HERE, on the machine that read it, keyed by project. It
 * is a cache and nothing more: the measurement remains the thing of record, and
 * everything below fails soft. A blocked IndexedDB, a private window, a full
 * quota, a browser that has never heard of it - every one of those returns null
 * or does nothing, and the panel goes back to naming the measurement in words.
 * Losing a cache entry costs a redraw; letting one throw would cost the panel.
 *
 * Not localStorage: a mesh is a Float32Array, and IndexedDB stores one by
 * structured clone, as the typed array it already is. localStorage would mean
 * base64 of the whole thing, in a synchronous 5 MB store, on the main thread.
 */

const DB_NAME = "pythia-site-models";
const DB_VERSION = 1;
const STORE = "meshes";

/**
 * The largest mesh worth keeping.
 *
 * A Float32Array of nine floats per triangle: 36 bytes each, so this is about
 * 1.4 million triangles - far past the point where the panel already warns that
 * measuring will be slow. The cap is here so that one enormous import cannot
 * quietly fill a user's disk quota and take the cache down for every other
 * project with it.
 */
export const MAX_CACHED_BYTES = 48 * 1024 * 1024;

function openDb() {
  return new Promise((resolve) => {
    let request;
    try {
      // Absent in some embedded webviews, and access alone THROWS in a Firefox
      // private window rather than returning undefined.
      if (typeof indexedDB === "undefined") return resolve(null);
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    // A database blocked by another tab mid-upgrade never fires either handler.
    // The panel is not waiting on this, but a promise that never settles keeps
    // its caller's closure alive for the life of the page.
    request.onblocked = () => resolve(null);
    return undefined;
  });
}

function run(db, mode, work) {
  return new Promise((resolve) => {
    let store;
    try {
      store = db.transaction(STORE, mode).objectStore(STORE);
    } catch {
      db.close();
      return resolve(null);
    }
    const request = work(store);
    request.onsuccess = () => { db.close(); resolve(request.result ?? null); };
    request.onerror = () => { db.close(); resolve(null); };
    return undefined;
  });
}

/** The cached model for a project, or null. Never throws. */
export async function loadMesh(key) {
  if (!key) return null;
  const db = await openDb();
  if (!db) return null;
  const record = await run(db, "readonly", (store) => store.get(String(key)));
  // A record from an older shape, or half-written: treated as absent rather
  // than handed to the parser, which would fail somewhere less obvious.
  if (!record || !(record.triangles instanceof Float32Array)) return null;
  if (record.triangles.length < 9 || record.triangles.length % 9 !== 0) return null;
  return {
    name: String(record.name || "site model.stl"),
    triangles: record.triangles,
    format: record.format === "ascii" ? "ascii" : "binary",
  };
}

/**
 * Keep a model for a project. Returns true when it was stored.
 *
 * Storing is best-effort by design: over quota, and the answer is that the
 * picture will not come back by itself next time, which the panel already says
 * in words. It is not a reason to refuse the import.
 */
export async function saveMesh(key, mesh) {
  if (!key || !mesh || !(mesh.triangles instanceof Float32Array)) return false;
  if (mesh.triangles.byteLength > MAX_CACHED_BYTES) return false;
  const db = await openDb();
  if (!db) return false;
  const stored = await run(db, "readwrite", (store) =>
    store.put(
      {
        name: mesh.name,
        // A copy, not the live array: the caller keeps drawing from theirs, and
        // a structured clone of a buffer that is later detached stores nothing.
        triangles: new Float32Array(mesh.triangles),
        format: mesh.format,
        savedAt: new Date().toISOString(),
      },
      String(key)
    )
  );
  return stored !== null;
}

/** Forget a project's model. Called when the user removes it. */
export async function deleteMesh(key) {
  if (!key) return;
  const db = await openDb();
  if (!db) return;
  await run(db, "readwrite", (store) => store.delete(String(key)));
}
